import React, { useState } from 'react'
import { UserProfile } from '../utils/analyzeAnswers'
import { trackButtonClick } from '../utils/tracking/tracking'
import './PersonalizedUpsellSlide.css'

interface PersonalizedUpsellSlideProps {
  userProfile: UserProfile
  onComplete?: () => void
}

export const PersonalizedUpsellSlide: React.FC<PersonalizedUpsellSlideProps> = ({
  userProfile,
  onComplete,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const getProblemAreas = (): string[] => {
    const areas: string[] = []
    if (userProfile.sleepIssue) areas.push('sleep')
    if (userProfile.stressIssue) areas.push('stress')
    if (userProfile.nutritionIssue) areas.push('nutrition')
    if (userProfile.movementIssue) areas.push('movement')
    return areas.slice(0, 3) // Top 3
  }

  const getProblemAreaLabel = (area: string): string => {
    switch (area) {
      case 'sleep':
        return '😴 Better Sleep'
      case 'stress':
        return '💆 Managing Stress'
      case 'nutrition':
        return '🥗 Nutrition & Hydration'
      case 'movement':
        return '🚶 Restoring Movement'
      default:
        return area
    }
  }

  const getProblemAreaDescription = (area: string): string => {
    switch (area) {
      case 'sleep':
        return 'Sleep through the night without nightmares. Our journal guides you through nervous system reset techniques.'
      case 'stress':
        return 'Break the anxiety cycle. Monthly journal + app give you daily tools designed for cancer trauma recovery.'
      case 'nutrition':
        return 'Rebuild your relationship with food and hydration. Guided journaling helps you listen to your body again.'
      case 'movement':
        return 'Reconnect with your body safely. Gentle movement practices integrated throughout the program.'
      default:
        return 'Personalized support for your unique journey.'
    }
  }

  const handleApplyDiscount = () => {
    setIsLoading(true)
    trackButtonClick('apply_50_discount_upsell')

    // Trigger quiz completion
    onComplete?.()

    // Get problem areas for URL
    const problemAreas = getProblemAreas()
    const areasParam = problemAreas.length > 0 ? `&areas=${problemAreas.join(',')}` : ''

    // Capture and pass through existing URL parameters (UTM, Facebook, etc.)
    const params = new URLSearchParams(window.location.search)
    const queryParts = ['source=quiz']

    // Add problem areas
    if (areasParam) queryParts.push(areasParam.substring(1)) // Remove leading &

    // Pass through tracking parameters
    if (params.get('utm_source')) queryParts.push(`utm_source=${encodeURIComponent(params.get('utm_source') || '')}`)
    if (params.get('utm_medium')) queryParts.push(`utm_medium=${encodeURIComponent(params.get('utm_medium') || '')}`)
    if (params.get('utm_campaign')) queryParts.push(`utm_campaign=${encodeURIComponent(params.get('utm_campaign') || '')}`)
    if (params.get('utm_content')) queryParts.push(`utm_content=${encodeURIComponent(params.get('utm_content') || '')}`)
    if (params.get('fbclid')) queryParts.push(`fbclid=${encodeURIComponent(params.get('fbclid') || '')}`)
    if (params.get('ad_id')) queryParts.push(`ad_id=${encodeURIComponent(params.get('ad_id') || '')}`)
    if (params.get('ad_name')) queryParts.push(`ad_name=${encodeURIComponent(params.get('ad_name') || '')}`)

    const queryString = queryParts.join('&')

    // Show loader briefly before redirect
    setTimeout(() => {
      window.location.href = `https://book.oncodiary.com/oncodiary-support-1mo-results?${queryString}`
    }, 1500)
  }

  const problemAreas = getProblemAreas()

  return (
    <>
      <div className="upsell-slide">
        <div className="upsell-content">
          <div className="upsell-container">
            {/* Header */}
            <div className="upsell-header">
              <h2>Your Personalized Wellness Plan</h2>
              <p className="upsell-subtitle">Recommended by Dr. Ellie Urban</p>
            </div>

            {/* Dr. Ellie Attribution */}
            <div className="ellie-badge">
              <div className="ellie-badge-content">
                <img
                  src="https://tfixaxzwymijtbizyexq.supabase.co/storage/v1/object/public/images/Ellie%20Urban%20small%202.webp"
                  alt="Dr. Ellie Urban"
                  className="ellie-photo"
                />
                <div className="ellie-text">
                  <p className="ellie-name">Dr. Ellie Urban</p>
                  <p className="ellie-title">Oncology Wellness Specialist</p>
                </div>
              </div>
            </div>

            {/* Problem Areas Section */}
            {problemAreas.length > 0 && (
              <div className="problem-areas">
                <h3>Based on your responses, we see you struggling with:</h3>
                <div className="areas-grid">
                  {problemAreas.map((area) => (
                    <div key={area} className="area-card">
                      <p className="area-label">{getProblemAreaLabel(area)}</p>
                      <p className="area-description">{getProblemAreaDescription(area)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Offer Card - scrolls with content */}
            <div className="offer-card">
              <div className="offer-banner">50% OFF for new member</div>

              <div className="offer-benefits">
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>Hardcover book + free shipping</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>Monthly wellness journal designed for you</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>Exclusive app access with daily practices</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>Cancel or pause anytime</span>
                </div>

              </div>

            </div>
            <div className="button-container">
              <button
                className="cta-button primary"
                onClick={handleApplyDiscount}
                disabled={isLoading}
              >
                {isLoading ? '✓ Applying Discount...' : 'Apply 50% Discount & Continue'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
